/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useRef, useMemo, useEffect, useCallback } from 'react';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import { getIsInJiraHoldoutData } from '@atlassian/holdout-utils/jira-holdout';
import type { ExperienceAttributes } from '../../common/types.tsx';
import { useExperienceContext } from '../../common/ui/use-experience-context/index.tsx';

export type ExperienceSuccessProps = {
	experience: string;
	attributes?: ExperienceAttributes;
};

const STABLE_EMPTY_OBJECT = {};
export const useExperienceSuccess = ({
	experience,
	attributes = STABLE_EMPTY_OBJECT,
}: ExperienceSuccessProps): ((extraAttributes?: ExperienceAttributes) => void) => {
	const experienceTracker = useExperienceContext();
	const route = useCurrentRoute();

	const self = useRef({
		experience,
		attributes,
		experienceTracker,
		route,
	});

	self.current.route = route;
	self.current.attributes = attributes;
	self.current.experienceTracker = experienceTracker;
	self.current.experience = experience;

	return useCallback(
		(extraAttributes: ExperienceAttributes = {}) => {
			const { current } = self;

			if (current.route && current.route.isRedirect === true) {
				return;
			}

			const shardAndRegionAttributes = {
				shard: getMeta('ajs-shard'),
				region: getMeta('ajs-region'),
			};
			const holdoutAttributes: ExperienceAttributes = {
				customHoldout: getIsInJiraHoldoutData().holdout,
			};
			current.experienceTracker.succeed({
				name: current.experience,
				attributes: {
					...current.attributes,
					...extraAttributes,
					...shardAndRegionAttributes,
					...holdoutAttributes,
				},
			});
		},
		// oxlint-disable-next-line react-hooks/exhaustive-deps
		[self],
	);
};

export const ExperienceSuccess = ({
	experience,
	attributes = STABLE_EMPTY_OBJECT,
}: ExperienceSuccessProps): null => {
	const onExperienceSuccess = useExperienceSuccess({
		experience,
		attributes,
	});

	useEffect(() => {
		onExperienceSuccess();
	}, [attributes, onExperienceSuccess]);

	useMemo(() => {
		if (__SERVER__) {
			onExperienceSuccess();
		}
	}, [onExperienceSuccess]);

	return null;
};
